import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaChild,
  FaPlus,
  FaSpinner,
  FaTimes,
  FaTrash,
  FaUser,
  FaUserFriends,
} from 'react-icons/fa';
import { API_URL } from '../../config';
import { Button, Select } from '../../styles/AdminStyles';
import { getAdminHeaders } from '../../pages/admin/campAdminShared';

const createEmptyChild = () => ({
  name: '',
  age: '',
  gender: '',
  hasAllergies: false,
  allergyDetails: '',
  tshirtSize: '',
  tshirtColor: '',
});

const createInitialFormData = () => ({
  registrationType: 'adult',
  branch: '',
  gender: '',
  fullName: '',
  phone: '',
  email: '',
  age: '',
  hasMedicalCondition: false,
  medicalConditionDetails: '',
  emergencyContact: {
    name: '',
    phone: '',
    relationship: '',
  },
  maritalStatus: 'single',
  hasSpouse: false,
  spouseInfo: {
    isElcMember: false,
    name: '',
    phone: '',
    hasAllergies: false,
    allergyDetails: '',
    tshirtSize: '',
    tshirtColor: '',
  },
  hasChildren: false,
  children: [],
  childrenHaveAllergies: false,
  childrenAllergyDetails: '',
  roomType: '',
  tshirtSize: '',
  tshirtColor: '',
  parentGuardianName: '',
  parentGuardianPhone: '',
  responsibleAdultName: '',
  responsibleAdultPhone: '',
  wantsToRegisterSiblings: false,
  foodSelections: {
    primary: { selections: [] },
    spouse: { selections: [] },
    children: [],
  },
});

const getActiveFoodDays = (config) => {
  if (!config?.foodConfiguration?.isEnabled) {
    return [];
  }

  return (config.foodConfiguration.days || [])
    .filter((day) => day.isActive !== false)
    .map((day) => {
      const snackMeal = day.meals?.find((meal) => meal.type === 'snack');
      const mainDishMeal = day.meals?.find((meal) => meal.type === 'mainDish');

      return {
        dayName: day.dayName,
        snackOptions: (snackMeal?.options || []).filter(
          (option) => option.isActive !== false && option.name,
        ),
        mainDishOptions: (mainDishMeal?.options || []).filter(
          (option) => option.isActive !== false && option.name,
        ),
        snackEnabled: snackMeal?.isEnabled !== false,
        mainDishEnabled: mainDishMeal?.isEnabled !== false,
      };
    });
};

const normalizeFoodSelections = (config, formData) => {
  const activeFoodDays = getActiveFoodDays(config);

  if (activeFoodDays.length === 0 || formData.registrationType !== 'adult') {
    return undefined;
  }

  const resolveSelections = (sourceSelections = []) =>
    activeFoodDays
      .map((day) => {
        const existing = sourceSelections.find(
          (selection) => selection.dayName === day.dayName,
        );

        const snackChoice =
          existing?.snackChoice ||
          (day.snackEnabled && day.snackOptions.length === 1
            ? day.snackOptions[0].name
            : undefined);
        const mainDishChoice =
          existing?.mainDishChoice ||
          (day.mainDishEnabled && day.mainDishOptions.length === 1
            ? day.mainDishOptions[0].name
            : undefined);

        if (!snackChoice && !mainDishChoice) {
          return null;
        }

        return {
          dayName: day.dayName,
          snackChoice,
          mainDishChoice,
        };
      })
      .filter(Boolean);

  const primarySelections = resolveSelections(
    formData.foodSelections?.primary?.selections || [],
  );
  const spouseSelections =
    formData.hasSpouse && formData.maritalStatus === 'married'
      ? resolveSelections(formData.foodSelections?.spouse?.selections || [])
      : [];

  return {
    primary: { selections: primarySelections },
    spouse: { selections: spouseSelections },
    children: [],
  };
};

const CampFallbackRegistrationModal = ({
  isOpen,
  onClose,
  onRegistered,
  suggestedName = '',
}) => {
  const [config, setConfig] = useState(null);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState(createInitialFormData());

  useEffect(() => {
    if (!isOpen) {
      setError('');
      setFieldErrors({});
      setAvailableRooms([]);
      setConfig(null);
      setFormData(createInitialFormData());
      return;
    }

    setFormData((current) => ({
      ...createInitialFormData(),
      fullName: suggestedName || '',
    }));
    setFieldErrors({});

    const loadConfig = async () => {
      try {
        setLoadingConfig(true);
        const response = await fetch(`${API_URL}/camp-registration/config`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || 'Failed to load camp configuration.');
        }

        setConfig(data);
      } catch (loadError) {
        console.error('Error loading camp config:', loadError);
        setError(loadError.message || 'Failed to load camp configuration.');
      } finally {
        setLoadingConfig(false);
      }
    };

    loadConfig();
  }, [isOpen, suggestedName]);

  useEffect(() => {
    if (!isOpen || !config || formData.registrationType !== 'adult') {
      return;
    }

    const loadRooms = async () => {
      try {
        const response = await fetch(`${API_URL}/camp-registration/filter-rooms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            maritalStatus: formData.maritalStatus,
            hasSpouse: formData.hasSpouse,
            hasChildren: formData.hasChildren,
          }),
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || 'Failed to load room options.');
        }

        setAvailableRooms(data);
        if (formData.roomType && !data.find((room) => room.name === formData.roomType)) {
          setFormData((current) => ({ ...current, roomType: '' }));
        }
      } catch (loadError) {
        console.error('Error loading room options:', loadError);
      }
    };

    loadRooms();
  }, [
    config,
    formData.registrationType,
    formData.maritalStatus,
    formData.hasSpouse,
    formData.hasChildren,
    formData.roomType,
    isOpen,
  ]);

  const foodDays = useMemo(() => getActiveFoodDays(config), [config]);

  const getFieldError = (path) => fieldErrors[path];

  const clearFieldError = (path) => {
    setFieldErrors((current) => {
      if (!current[path]) {
        return current;
      }

      const nextErrors = { ...current };
      delete nextErrors[path];
      return nextErrors;
    });
  };

  const updateForm = (name, value) => {
    clearFieldError(name);
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const updateNestedField = (parent, child, value) => {
    clearFieldError(`${parent}.${child}`);
    setFormData((current) => ({
      ...current,
      [parent]: {
        ...current[parent],
        [child]: value,
      },
    }));
  };

  const updateChild = (index, field, value) => {
    clearFieldError(`children.${index}.${field}`);
    clearFieldError('children');
    setFormData((current) => ({
      ...current,
      children: current.children.map((child, childIndex) =>
        childIndex === index
          ? {
              ...child,
              [field]: value,
            }
          : child,
      ),
    }));
  };

  const addChild = () => {
    clearFieldError('children');
    setFormData((current) => ({
      ...current,
      children: [...current.children, createEmptyChild()],
    }));
  };

  const removeChild = (index) => {
    clearFieldError('children');
    clearFieldError(`children.${index}.name`);
    clearFieldError(`children.${index}.age`);
    setFormData((current) => ({
      ...current,
      children: current.children.filter((_, childIndex) => childIndex !== index),
    }));
  };

  const updateFoodSelection = (personType, dayName, field, value) => {
    setFormData((current) => {
      const selections = [
        ...(current.foodSelections?.[personType]?.selections || []),
      ];
      const existingIndex = selections.findIndex(
        (selection) => selection.dayName === dayName,
      );

      if (existingIndex >= 0) {
        selections[existingIndex] = {
          ...selections[existingIndex],
          [field]: value,
        };
      } else {
        selections.push({ dayName, [field]: value });
      }

      return {
        ...current,
        foodSelections: {
          ...current.foodSelections,
          [personType]: { selections },
        },
      };
    });
  };

  const getFoodSelectionValue = (personType, dayName, field) => {
    const selection = formData.foodSelections?.[personType]?.selections?.find(
      (entry) => entry.dayName === dayName,
    );
    return selection?.[field] || '';
  };

  const buildAdultPayload = () => ({
    registrationType: 'adult',
    branch: formData.branch,
    gender: formData.gender,
    fullName: formData.fullName.trim(),
    phone: formData.phone.trim(),
    email: formData.email.trim() || undefined,
    hasMedicalCondition: formData.hasMedicalCondition,
    medicalConditionDetails: formData.hasMedicalCondition
      ? formData.medicalConditionDetails.trim()
      : '',
    emergencyContact: {
      name: formData.emergencyContact.name.trim(),
      phone: formData.emergencyContact.phone.trim(),
      relationship: formData.emergencyContact.relationship.trim(),
    },
    maritalStatus: formData.maritalStatus,
    hasSpouse: formData.maritalStatus === 'married' ? formData.hasSpouse : false,
    spouseInfo:
      formData.maritalStatus === 'married' && formData.hasSpouse
        ? {
            isElcMember: formData.spouseInfo.isElcMember,
            name: formData.spouseInfo.name.trim(),
            phone: formData.spouseInfo.phone.trim(),
            hasAllergies: formData.spouseInfo.hasAllergies,
            allergyDetails: formData.spouseInfo.hasAllergies
              ? formData.spouseInfo.allergyDetails.trim()
              : '',
            tshirtSize: formData.spouseInfo.tshirtSize,
            tshirtColor: formData.spouseInfo.tshirtColor,
          }
        : undefined,
    hasChildren: formData.hasChildren,
    children: formData.hasChildren
      ? formData.children.map((child) => ({
          ...child,
          name: child.name.trim(),
          age: Number(child.age),
          allergyDetails: child.hasAllergies ? child.allergyDetails.trim() : '',
        }))
      : [],
    childrenHaveAllergies: formData.childrenHaveAllergies,
    childrenAllergyDetails: formData.childrenHaveAllergies
      ? formData.childrenAllergyDetails.trim()
      : '',
    roomType: formData.roomType,
    tshirtSize: formData.tshirtSize,
    tshirtColor: formData.tshirtColor,
    foodSelections: normalizeFoodSelections(config, formData),
  });

  const buildChildPayload = () => ({
    registrationType: 'child',
    branch: formData.branch,
    gender: formData.gender,
    fullName: formData.fullName.trim(),
    age: Number(formData.age),
    email: formData.email.trim() || undefined,
    hasMedicalCondition: formData.hasMedicalCondition,
    medicalConditionDetails: formData.hasMedicalCondition
      ? formData.medicalConditionDetails.trim()
      : '',
    parentGuardianName: formData.parentGuardianName.trim(),
    parentGuardianPhone: formData.parentGuardianPhone.trim(),
    responsibleAdultName: formData.responsibleAdultName.trim(),
    responsibleAdultPhone: formData.responsibleAdultPhone.trim(),
    wantsToRegisterSiblings: formData.wantsToRegisterSiblings,
    children: formData.wantsToRegisterSiblings
      ? formData.children.map((child) => ({
          ...child,
          name: child.name.trim(),
          age: Number(child.age),
          allergyDetails: child.hasAllergies ? child.allergyDetails.trim() : '',
        }))
      : [],
    childrenHaveAllergies: formData.childrenHaveAllergies,
    childrenAllergyDetails: formData.childrenHaveAllergies
      ? formData.childrenAllergyDetails.trim()
      : '',
  });

  const validateAdultForm = () => {
    const errors = {};

    if (!formData.branch) {
      errors.branch = 'Branch is required.';
    }

    if (!formData.gender) {
      errors.gender = 'Gender is required.';
    }

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required.';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone is required.';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required.';
    }

    if (!formData.emergencyContact.name.trim()) {
      errors['emergencyContact.name'] = 'Emergency contact name is required.';
    }

    if (!formData.emergencyContact.phone.trim()) {
      errors['emergencyContact.phone'] = 'Emergency contact phone is required.';
    }

    if (formData.hasMedicalCondition && !formData.medicalConditionDetails.trim()) {
      errors.medicalConditionDetails =
        'Medical condition details are required.';
    }

    if (!formData.tshirtSize) {
      errors.tshirtSize = 'T-shirt size is required.';
    }

    if (!formData.tshirtColor) {
      errors.tshirtColor = 'T-shirt color is required.';
    }

    if (!formData.roomType) {
      errors.roomType = 'Room package is required.';
    }

    if (formData.maritalStatus === 'married' && formData.hasSpouse) {
      if (!formData.spouseInfo.name.trim()) {
        errors['spouseInfo.name'] = 'Spouse name is required.';
      }

      if (!formData.spouseInfo.tshirtSize) {
        errors['spouseInfo.tshirtSize'] = 'Spouse t-shirt size is required.';
      }

      if (!formData.spouseInfo.tshirtColor) {
        errors['spouseInfo.tshirtColor'] = 'Spouse t-shirt color is required.';
      }

      if (
        formData.spouseInfo.hasAllergies &&
        !formData.spouseInfo.allergyDetails.trim()
      ) {
        errors['spouseInfo.allergyDetails'] =
          'Spouse allergy details are required.';
      }
    }

    if (formData.hasChildren) {
      if (!formData.children.length) {
        errors.children = 'Add at least one child or turn off children.';
      }

      formData.children.forEach((child, index) => {
        if (!child.name.trim()) {
          errors[`children.${index}.name`] = 'Child name is required.';
        }

        if (Number.isNaN(Number(child.age)) || child.age === '') {
          errors[`children.${index}.age`] = 'Child age is required.';
        }
      });
    }

    return errors;
  };

  const validateChildForm = () => {
    const errors = {};
    const age = Number(formData.age);

    if (!formData.branch) {
      errors.branch = 'Branch is required.';
    }

    if (!formData.gender) {
      errors.gender = 'Gender is required.';
    }

    if (!formData.fullName.trim()) {
      errors.fullName = 'Child name is required.';
    }

    if (formData.age === '' || Number.isNaN(age)) {
      errors.age = 'Child age is required.';
    } else if (age < 9 || age > 15) {
      errors.age = 'Independent child registration requires an age between 9 and 15.';
    }

    if (!formData.parentGuardianName.trim()) {
      errors.parentGuardianName = 'Parent/guardian name is required.';
    }

    if (!formData.parentGuardianPhone.trim()) {
      errors.parentGuardianPhone = 'Parent/guardian phone is required.';
    }

    if (!formData.responsibleAdultName.trim()) {
      errors.responsibleAdultName = 'Responsible adult name is required.';
    }

    if (!formData.responsibleAdultPhone.trim()) {
      errors.responsibleAdultPhone = 'Responsible adult phone is required.';
    }

    if (formData.hasMedicalCondition && !formData.medicalConditionDetails.trim()) {
      errors.medicalConditionDetails =
        'Medical condition details are required.';
    }

    if (formData.wantsToRegisterSiblings) {
      if (!formData.children.length) {
        errors.children = 'Add at least one sibling or turn off siblings.';
      }

      formData.children.forEach((child, index) => {
        if (!child.name.trim()) {
          errors[`children.${index}.name`] = 'Sibling name is required.';
        }

        if (Number.isNaN(Number(child.age)) || child.age === '') {
          errors[`children.${index}.age`] = 'Sibling age is required.';
        }
      });
    }

    return errors;
  };

  const handleSubmit = async () => {
    const nextFieldErrors =
      formData.registrationType === 'adult'
        ? validateAdultForm()
        : validateChildForm();

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError('');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setFieldErrors({});
      const payload =
        formData.registrationType === 'adult'
          ? buildAdultPayload()
          : buildChildPayload();

      const response = await fetch(`${API_URL}/camp-registration/admin/register`, {
        method: 'POST',
        headers: getAdminHeaders(true),
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || 'Failed to register camper.');
      }

      onRegistered?.(data, payload);
      onClose();
    } catch (submitError) {
      console.error('Error registering camper:', submitError);
      setError(submitError.message || 'Failed to register camper.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <AnimatePresence>
      <Overlay
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          as={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          onClick={(event) => event.stopPropagation()}
        >
          <Header>
            <div>
              <h2>Register Camper</h2>
              <p>Create a missing camper record directly from check-in.</p>
            </div>
            <CloseButton type="button" onClick={onClose}>
              <FaTimes />
            </CloseButton>
          </Header>

          <Body>
            {error && <ErrorBanner>{error}</ErrorBanner>}

            {loadingConfig ? (
              <LoadingState>
                <FaSpinner className="spin" />
                <span>Loading registration settings...</span>
              </LoadingState>
            ) : (
              <>
                <Section>
                  <SectionTitle>Registration Type</SectionTitle>
                  <Grid $columns="repeat(auto-fit, minmax(180px, 1fr))">
                    <TypeButton
                      type="button"
                      $active={formData.registrationType === 'adult'}
                      onClick={() =>
                        setFormData((current) => ({
                          ...createInitialFormData(),
                          registrationType: 'adult',
                          fullName: current.fullName,
                        }))
                      }
                    >
                      <FaUser />
                      <span>Adult Registration</span>
                    </TypeButton>
                    <TypeButton
                      type="button"
                      $active={formData.registrationType === 'child'}
                      onClick={() =>
                        setFormData((current) => ({
                          ...createInitialFormData(),
                          registrationType: 'child',
                          fullName: current.fullName,
                        }))
                      }
                    >
                      <FaChild />
                      <span>Child Registration</span>
                    </TypeButton>
                  </Grid>
                </Section>

                <Section>
                  <SectionTitle>Basic Information</SectionTitle>
                  <Grid>
                    <Field $error={!!getFieldError('branch')}>
                      <label>Branch</label>
                      <Select
                        value={formData.branch}
                        onChange={(event) => updateForm('branch', event.target.value)}
                      >
                        <option value="">Select branch</option>
                        {[
                          'Headquarters',
                          'Adenta',
                          'Sunyani',
                          'KNUST',
                          'Not a member',
                        ].map((branch) => (
                          <option key={branch} value={branch}>
                            {branch}
                          </option>
                        ))}
                      </Select>
                      <FieldError>{getFieldError('branch')}</FieldError>
                    </Field>

                    <Field $error={!!getFieldError('gender')}>
                      <label>Gender</label>
                      <Select
                        value={formData.gender}
                        onChange={(event) => updateForm('gender', event.target.value)}
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </Select>
                      <FieldError>{getFieldError('gender')}</FieldError>
                    </Field>

                    <Field $error={!!getFieldError('fullName')}>
                      <label>
                        {formData.registrationType === 'child'
                          ? 'Child Name'
                          : 'Full Name'}
                      </label>
                      <input
                        value={formData.fullName}
                        onChange={(event) => updateForm('fullName', event.target.value)}
                      />
                      <FieldError>{getFieldError('fullName')}</FieldError>
                    </Field>

                    {formData.registrationType === 'adult' ? (
                      <>
                        <Field $error={!!getFieldError('phone')}>
                          <label>Phone</label>
                          <input
                            value={formData.phone}
                            onChange={(event) =>
                              updateForm('phone', event.target.value)
                            }
                          />
                          <FieldError>{getFieldError('phone')}</FieldError>
                        </Field>

                        <Field $error={!!getFieldError('email')}>
                          <label>Email</label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(event) =>
                              updateForm('email', event.target.value)
                            }
                          />
                          <FieldError>{getFieldError('email')}</FieldError>
                        </Field>
                      </>
                    ) : (
                      <>
                        <Field $error={!!getFieldError('age')}>
                          <label>Child Age</label>
                          <input
                            type="number"
                            min="9"
                            max="15"
                            value={formData.age}
                            onChange={(event) => updateForm('age', event.target.value)}
                          />
                          <FieldError>{getFieldError('age')}</FieldError>
                        </Field>

                        <Field>
                          <label>Email (Optional)</label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(event) =>
                              updateForm('email', event.target.value)
                            }
                          />
                        </Field>
                      </>
                    )}

                    <Field $fullWidth>
                      <CheckboxRow>
                        <input
                          type="checkbox"
                          checked={formData.hasMedicalCondition}
                          onChange={(event) =>
                            updateForm('hasMedicalCondition', event.target.checked)
                          }
                        />
                        <span>Camper has a medical condition</span>
                      </CheckboxRow>
                    </Field>

                    {formData.hasMedicalCondition && (
                      <Field
                        $fullWidth
                        $error={!!getFieldError('medicalConditionDetails')}
                      >
                        <label>Medical Condition Details</label>
                        <textarea
                          rows="3"
                          value={formData.medicalConditionDetails}
                          onChange={(event) =>
                            updateForm('medicalConditionDetails', event.target.value)
                          }
                        />
                        <FieldError>
                          {getFieldError('medicalConditionDetails')}
                        </FieldError>
                      </Field>
                    )}
                  </Grid>
                </Section>

                {formData.registrationType === 'adult' ? (
                  <>
                    <Section>
                      <SectionTitle>Emergency Contact</SectionTitle>
                      <Grid>
                        <Field $error={!!getFieldError('emergencyContact.name')}>
                          <label>Name</label>
                          <input
                            value={formData.emergencyContact.name}
                            onChange={(event) =>
                              updateNestedField(
                                'emergencyContact',
                                'name',
                                event.target.value,
                              )
                            }
                          />
                          <FieldError>
                            {getFieldError('emergencyContact.name')}
                          </FieldError>
                        </Field>
                        <Field $error={!!getFieldError('emergencyContact.phone')}>
                          <label>Phone</label>
                          <input
                            value={formData.emergencyContact.phone}
                            onChange={(event) =>
                              updateNestedField(
                                'emergencyContact',
                                'phone',
                                event.target.value,
                              )
                            }
                          />
                          <FieldError>
                            {getFieldError('emergencyContact.phone')}
                          </FieldError>
                        </Field>
                        <Field $error={!!getFieldError('roomType')}>
                          <label>Relationship</label>
                          <input
                            value={formData.emergencyContact.relationship}
                            onChange={(event) =>
                              updateNestedField(
                                'emergencyContact',
                                'relationship',
                                event.target.value,
                              )
                            }
                          />
                        </Field>
                      </Grid>
                    </Section>

                    <Section>
                      <SectionTitle>Room and T-Shirt</SectionTitle>
                      <Grid>
                        <Field>
                          <label>Marital Status</label>
                          <Select
                            value={formData.maritalStatus}
                            onChange={(event) =>
                              updateForm('maritalStatus', event.target.value)
                            }
                          >
                            <option value="single">Single</option>
                            <option value="married">Married</option>
                          </Select>
                        </Field>

                        <Field>
                          <label>Room Package</label>
                          <Select
                            value={formData.roomType}
                            onChange={(event) => updateForm('roomType', event.target.value)}
                          >
                            <option value="">Select room</option>
                            {availableRooms.map((room) => (
                              <option key={room.name} value={room.name}>
                                {room.name} - GHC {room.price}
                              </option>
                            ))}
                          </Select>
                          <FieldError>{getFieldError('roomType')}</FieldError>
                        </Field>

                        <Field $error={!!getFieldError('tshirtSize')}>
                          <label>T-Shirt Size</label>
                          <Select
                            value={formData.tshirtSize}
                            onChange={(event) =>
                              updateForm('tshirtSize', event.target.value)
                            }
                          >
                            <option value="">Select size</option>
                            {(config?.tshirtSizes || []).map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                              ))}
                            </Select>
                          <FieldError>{getFieldError('tshirtSize')}</FieldError>
                        </Field>

                        <Field $error={!!getFieldError('tshirtColor')}>
                          <label>T-Shirt Color</label>
                          <Select
                            value={formData.tshirtColor}
                            onChange={(event) =>
                              updateForm('tshirtColor', event.target.value)
                            }
                          >
                            <option value="">Select color</option>
                            {(config?.tshirtColors || []).map((color) => (
                              <option key={color} value={color}>
                                {color}
                              </option>
                              ))}
                            </Select>
                          <FieldError>{getFieldError('tshirtColor')}</FieldError>
                        </Field>

                        <Field $fullWidth>
                          <CheckboxRow>
                            <input
                              type="checkbox"
                              checked={
                                formData.maritalStatus === 'married' && formData.hasSpouse
                              }
                              onChange={(event) =>
                                updateForm('hasSpouse', event.target.checked)
                              }
                              disabled={formData.maritalStatus !== 'married'}
                            />
                            <span>Add spouse to this registration</span>
                          </CheckboxRow>
                        </Field>

                        <Field $fullWidth>
                          <CheckboxRow>
                            <input
                              type="checkbox"
                              checked={formData.hasChildren}
                              onChange={(event) =>
                                updateForm('hasChildren', event.target.checked)
                              }
                            />
                            <span>Add children to this registration</span>
                          </CheckboxRow>
                        </Field>
                      </Grid>
                    </Section>

                    {formData.maritalStatus === 'married' && formData.hasSpouse && (
                      <Section>
                        <SectionTitle>Spouse Information</SectionTitle>
                        <Grid>
                          <Field $error={!!getFieldError('spouseInfo.name')}>
                            <label>Spouse Name</label>
                            <input
                              value={formData.spouseInfo.name}
                              onChange={(event) =>
                                updateNestedField(
                                  'spouseInfo',
                                  'name',
                                  event.target.value,
                                )
                              }
                            />
                            <FieldError>{getFieldError('spouseInfo.name')}</FieldError>
                          </Field>
                          <Field
                            $error={!!getFieldError('spouseInfo.tshirtSize')}
                          >
                            <label>Spouse Phone</label>
                            <input
                              value={formData.spouseInfo.phone}
                              onChange={(event) =>
                                updateNestedField(
                                  'spouseInfo',
                                  'phone',
                                  event.target.value,
                                )
                              }
                            />
                          </Field>
                          <Field>
                            <label>Spouse T-Shirt Size</label>
                            <Select
                              value={formData.spouseInfo.tshirtSize}
                              onChange={(event) =>
                                updateNestedField(
                                  'spouseInfo',
                                  'tshirtSize',
                                  event.target.value,
                                )
                              }
                            >
                              <option value="">Select size</option>
                              {(config?.tshirtSizes || []).map((size) => (
                                <option key={size} value={size}>
                                  {size}
                                </option>
                                ))}
                              </Select>
                            <FieldError>
                              {getFieldError('spouseInfo.tshirtSize')}
                            </FieldError>
                          </Field>
                          <Field
                            $error={!!getFieldError('spouseInfo.tshirtColor')}
                          >
                            <label>Spouse T-Shirt Color</label>
                            <Select
                              value={formData.spouseInfo.tshirtColor}
                              onChange={(event) =>
                                updateNestedField(
                                  'spouseInfo',
                                  'tshirtColor',
                                  event.target.value,
                                )
                              }
                            >
                              <option value="">Select color</option>
                              {(config?.tshirtColors || []).map((color) => (
                                <option key={color} value={color}>
                                  {color}
                                </option>
                                ))}
                              </Select>
                            <FieldError>
                              {getFieldError('spouseInfo.tshirtColor')}
                            </FieldError>
                          </Field>
                          <Field $fullWidth>
                            <CheckboxRow>
                              <input
                                type="checkbox"
                                checked={formData.spouseInfo.hasAllergies}
                                onChange={(event) =>
                                  updateNestedField(
                                    'spouseInfo',
                                    'hasAllergies',
                                    event.target.checked,
                                  )
                                }
                              />
                              <span>Spouse has allergies</span>
                            </CheckboxRow>
                          </Field>
                          {formData.spouseInfo.hasAllergies && (
                            <Field
                              $fullWidth
                              $error={!!getFieldError('spouseInfo.allergyDetails')}
                            >
                              <label>Spouse Allergy Details</label>
                              <textarea
                                rows="3"
                                value={formData.spouseInfo.allergyDetails}
                                onChange={(event) =>
                                  updateNestedField(
                                    'spouseInfo',
                                    'allergyDetails',
                                    event.target.value,
                                  )
                                }
                              />
                              <FieldError>
                                {getFieldError('spouseInfo.allergyDetails')}
                              </FieldError>
                            </Field>
                          )}
                        </Grid>
                      </Section>
                    )}

                    {formData.hasChildren && (
                      <Section>
                        <SectionTitle>Children</SectionTitle>
                        <SectionHelp>
                          Add each child included on this adult registration.
                        </SectionHelp>
                        <InlineActions>
                          <Button type="button" onClick={addChild}>
                            <FaPlus /> Add Child
                          </Button>
                        </InlineActions>
                        <FieldError>{getFieldError('children')}</FieldError>

                        {formData.children.map((child, index) => (
                          <NestedCard key={`adult-child-${index}`}>
                            <NestedHeader>
                              <strong>Child {index + 1}</strong>
                              <IconAction
                                type="button"
                                onClick={() => removeChild(index)}
                              >
                                <FaTrash />
                              </IconAction>
                            </NestedHeader>
                            <Grid>
                              <Field
                                $error={!!getFieldError(`children.${index}.name`)}
                              >
                                <label>Name</label>
                                <input
                                  value={child.name}
                                  onChange={(event) =>
                                    updateChild(index, 'name', event.target.value)
                                  }
                                />
                                <FieldError>
                                  {getFieldError(`children.${index}.name`)}
                                </FieldError>
                              </Field>
                              <Field
                                $error={!!getFieldError(`children.${index}.age`)}
                              >
                                <label>Age</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="15"
                                  value={child.age}
                                  onChange={(event) =>
                                    updateChild(index, 'age', event.target.value)
                                  }
                                />
                                <FieldError>
                                  {getFieldError(`children.${index}.age`)}
                                </FieldError>
                              </Field>
                              <Field>
                                <label>Gender</label>
                                <Select
                                  value={child.gender}
                                  onChange={(event) =>
                                    updateChild(index, 'gender', event.target.value)
                                  }
                                >
                                  <option value="">Select gender</option>
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                </Select>
                              </Field>
                            </Grid>
                          </NestedCard>
                        ))}
                      </Section>
                    )}

                    {foodDays.length > 0 && (
                      <Section>
                        <SectionTitle>Food Selections</SectionTitle>
                        <SectionHelp>
                          Record meal preferences here when your camp food setup
                          requires them.
                        </SectionHelp>

                        <FoodBlock>
                          <h4>Primary Camper</h4>
                          {foodDays.map((day) => (
                            <FoodDayRow key={`primary-${day.dayName}`}>
                              <strong>{day.dayName}</strong>
                              {day.snackEnabled && day.snackOptions.length > 0 && (
                                <Select
                                  value={getFoodSelectionValue(
                                    'primary',
                                    day.dayName,
                                    'snackChoice',
                                  )}
                                  onChange={(event) =>
                                    updateFoodSelection(
                                      'primary',
                                      day.dayName,
                                      'snackChoice',
                                      event.target.value,
                                    )
                                  }
                                >
                                  <option value="">Snack choice</option>
                                  {day.snackOptions.map((option) => (
                                    <option key={option.name} value={option.name}>
                                      {option.name}
                                    </option>
                                  ))}
                                </Select>
                              )}
                              {day.mainDishEnabled &&
                                day.mainDishOptions.length > 0 && (
                                  <Select
                                    value={getFoodSelectionValue(
                                      'primary',
                                      day.dayName,
                                      'mainDishChoice',
                                    )}
                                    onChange={(event) =>
                                      updateFoodSelection(
                                        'primary',
                                        day.dayName,
                                        'mainDishChoice',
                                        event.target.value,
                                      )
                                    }
                                  >
                                    <option value="">Main dish choice</option>
                                    {day.mainDishOptions.map((option) => (
                                      <option key={option.name} value={option.name}>
                                        {option.name}
                                      </option>
                                    ))}
                                  </Select>
                                )}
                            </FoodDayRow>
                          ))}
                        </FoodBlock>

                        {formData.maritalStatus === 'married' &&
                          formData.hasSpouse && (
                            <FoodBlock>
                              <h4>Spouse</h4>
                              {foodDays.map((day) => (
                                <FoodDayRow key={`spouse-${day.dayName}`}>
                                  <strong>{day.dayName}</strong>
                                  {day.snackEnabled &&
                                    day.snackOptions.length > 0 && (
                                      <Select
                                        value={getFoodSelectionValue(
                                          'spouse',
                                          day.dayName,
                                          'snackChoice',
                                        )}
                                        onChange={(event) =>
                                          updateFoodSelection(
                                            'spouse',
                                            day.dayName,
                                            'snackChoice',
                                            event.target.value,
                                          )
                                        }
                                      >
                                        <option value="">Snack choice</option>
                                        {day.snackOptions.map((option) => (
                                          <option
                                            key={option.name}
                                            value={option.name}
                                          >
                                            {option.name}
                                          </option>
                                        ))}
                                      </Select>
                                    )}
                                  {day.mainDishEnabled &&
                                    day.mainDishOptions.length > 0 && (
                                      <Select
                                        value={getFoodSelectionValue(
                                          'spouse',
                                          day.dayName,
                                          'mainDishChoice',
                                        )}
                                        onChange={(event) =>
                                          updateFoodSelection(
                                            'spouse',
                                            day.dayName,
                                            'mainDishChoice',
                                            event.target.value,
                                          )
                                        }
                                      >
                                        <option value="">Main dish choice</option>
                                        {day.mainDishOptions.map((option) => (
                                          <option
                                            key={option.name}
                                            value={option.name}
                                          >
                                            {option.name}
                                          </option>
                                        ))}
                                      </Select>
                                    )}
                                </FoodDayRow>
                              ))}
                            </FoodBlock>
                          )}
                      </Section>
                    )}
                  </>
                ) : (
                  <>
                    <Section>
                      <SectionTitle>Child Registration Contacts</SectionTitle>
                      <Grid>
                        <Field $error={!!getFieldError('parentGuardianName')}>
                          <label>Parent/Guardian Name</label>
                          <input
                            value={formData.parentGuardianName}
                            onChange={(event) =>
                              updateForm('parentGuardianName', event.target.value)
                            }
                          />
                          <FieldError>
                            {getFieldError('parentGuardianName')}
                          </FieldError>
                        </Field>
                        <Field $error={!!getFieldError('parentGuardianPhone')}>
                          <label>Parent/Guardian Phone</label>
                          <input
                            value={formData.parentGuardianPhone}
                            onChange={(event) =>
                              updateForm('parentGuardianPhone', event.target.value)
                            }
                          />
                          <FieldError>
                            {getFieldError('parentGuardianPhone')}
                          </FieldError>
                        </Field>
                        <Field $error={!!getFieldError('responsibleAdultName')}>
                          <label>Responsible Adult Name</label>
                          <input
                            value={formData.responsibleAdultName}
                            onChange={(event) =>
                              updateForm(
                                'responsibleAdultName',
                                event.target.value,
                              )
                            }
                          />
                          <FieldError>
                            {getFieldError('responsibleAdultName')}
                          </FieldError>
                        </Field>
                        <Field $error={!!getFieldError('responsibleAdultPhone')}>
                          <label>Responsible Adult Phone</label>
                          <input
                            value={formData.responsibleAdultPhone}
                            onChange={(event) =>
                              updateForm(
                                'responsibleAdultPhone',
                                event.target.value,
                              )
                            }
                          />
                          <FieldError>
                            {getFieldError('responsibleAdultPhone')}
                          </FieldError>
                        </Field>
                      </Grid>
                    </Section>

                    <Section>
                      <SectionTitle>Siblings</SectionTitle>
                      <CheckboxRow>
                        <input
                          type="checkbox"
                          checked={formData.wantsToRegisterSiblings}
                          onChange={(event) =>
                            updateForm(
                              'wantsToRegisterSiblings',
                              event.target.checked,
                            )
                          }
                        />
                        <span>Register siblings with this camper</span>
                      </CheckboxRow>

                      {formData.wantsToRegisterSiblings && (
                        <>
                          <InlineActions>
                            <Button type="button" onClick={addChild}>
                              <FaPlus /> Add Sibling
                            </Button>
                          </InlineActions>
                          <FieldError>{getFieldError('children')}</FieldError>

                          {formData.children.map((child, index) => (
                            <NestedCard key={`sibling-${index}`}>
                              <NestedHeader>
                                <strong>Sibling {index + 1}</strong>
                                <IconAction
                                  type="button"
                                  onClick={() => removeChild(index)}
                                >
                                  <FaTrash />
                                </IconAction>
                              </NestedHeader>
                              <Grid>
                                <Field
                                  $error={!!getFieldError(`children.${index}.name`)}
                                >
                                  <label>Name</label>
                                  <input
                                    value={child.name}
                                    onChange={(event) =>
                                      updateChild(index, 'name', event.target.value)
                                    }
                                  />
                                  <FieldError>
                                    {getFieldError(`children.${index}.name`)}
                                  </FieldError>
                                </Field>
                                <Field
                                  $error={!!getFieldError(`children.${index}.age`)}
                                >
                                  <label>Age</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="15"
                                    value={child.age}
                                    onChange={(event) =>
                                      updateChild(index, 'age', event.target.value)
                                    }
                                  />
                                  <FieldError>
                                    {getFieldError(`children.${index}.age`)}
                                  </FieldError>
                                </Field>
                                <Field>
                                  <label>Gender</label>
                                  <Select
                                    value={child.gender}
                                    onChange={(event) =>
                                      updateChild(index, 'gender', event.target.value)
                                    }
                                  >
                                    <option value="">Select gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                  </Select>
                                </Field>
                              </Grid>
                            </NestedCard>
                          ))}
                        </>
                      )}
                    </Section>
                  </>
                )}
              </>
            )}
          </Body>

          <Footer>
            <Button $variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || loadingConfig}>
              {submitting ? (
                <>
                  <FaSpinner className="spin" /> Saving
                </>
              ) : (
                'Register Camper'
              )}
            </Button>
          </Footer>
        </Modal>
      </Overlay>
    </AnimatePresence>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.58);
`;

const Modal = styled.div`
  width: min(980px, 100%);
  max-height: 92vh;
  overflow: auto;
  background: white;
  border-radius: 24px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding: 1.4rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;

  h2 {
    margin: 0;
    color: #0f172a;
  }

  p {
    margin: 0.35rem 0 0;
    color: #64748b;
  }
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 1.5rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem 1.5rem;
  border-top: 1px solid #e2e8f0;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
`;

const Section = styled.section`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.85rem;
  color: #0f172a;
  font-size: 1rem;
`;

const SectionHelp = styled.p`
  margin: 0 0 1rem;
  color: #64748b;
  font-size: 0.92rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$columns || 'repeat(auto-fit, minmax(220px, 1fr))'};
  gap: 1rem;
`;

const Field = styled.div`
  ${(props) => (props.$fullWidth ? 'grid-column: 1 / -1;' : '')}

  label {
    display: block;
    margin-bottom: 0.45rem;
    color: #334155;
    font-weight: 700;
    font-size: 0.92rem;
  }

  input,
  textarea,
  select {
    width: 100%;
    border: 1px solid ${(props) => (props.$error ? '#fca5a5' : '#dbe2ea')};
    border-radius: 12px;
    padding: 0.85rem 0.95rem;
    font-size: 0.95rem;
    color: #0f172a;
    background: white;
  }

  textarea {
    resize: vertical;
  }
`;

const FieldError = styled.div`
  min-height: 1.1rem;
  margin-top: 0.35rem;
  color: #dc2626;
  font-size: 0.82rem;
  font-weight: 600;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  color: #334155;
  font-weight: 600;
  cursor: pointer;
`;

const TypeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem;
  border-radius: 16px;
  border: 1px solid ${(props) => (props.$active ? '#bfdbfe' : '#dbe2ea')};
  background: ${(props) => (props.$active ? '#eff6ff' : 'white')};
  color: ${(props) => (props.$active ? '#1d4ed8' : '#0f172a')};
  cursor: pointer;
  font-weight: 700;
`;

const ErrorBanner = styled.div`
  margin-bottom: 1rem;
  padding: 0.95rem 1rem;
  border-radius: 14px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
`;

const LoadingState = styled.div`
  min-height: 220px;
  display: grid;
  place-items: center;
  gap: 0.8rem;
  color: #64748b;
  font-weight: 600;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
`;

const InlineActions = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem;
`;

const NestedCard = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const NestedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  color: #0f172a;
`;

const IconAction = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: #fee2e2;
  color: #b91c1c;
  cursor: pointer;
`;

const FoodBlock = styled.div`
  padding: 1rem;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  margin-bottom: 1rem;

  h4 {
    margin: 0 0 0.9rem;
    color: #0f172a;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const FoodDayRow = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 160px) repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.75rem;

  strong {
    color: #334155;
    font-size: 0.92rem;
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export default CampFallbackRegistrationModal;
